import React from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Kpis, Panel, Empty } from '../../ui/kit.js';
import { useBuilding, day } from '../../state/building.js';

import Skyline from '../../media/images/modules/building-skyline.jpg';

import { ReactComponent as Handyman } from '../../media/images/svg/handyman.svg';
import { ReactComponent as Engineering } from '../../media/images/svg/engineering.svg';
import { ReactComponent as Cleaning } from '../../media/images/svg/cleaning_services.svg';
import { ReactComponent as TaskAlt } from '../../media/images/svg/task_alt.svg';
import { ReactComponent as Storefront } from '../../media/images/svg/storefront.svg';
import { ReactComponent as Inventory } from '../../media/images/svg/inventory_2.svg';
import { ReactComponent as FactCheck } from '../../media/images/svg/fact_check.svg';
import { ReactComponent as Forklift } from '../../media/images/svg/forklift.svg';
import { ReactComponent as Badge } from '../../media/images/svg/badge.svg';
import { ReactComponent as Key } from '../../media/images/svg/key.svg';
import { ReactComponent as Parking } from '../../media/images/svg/local_parking.svg';
import { ReactComponent as Report } from '../../media/images/svg/report.svg';
import { ReactComponent as Campaign } from '../../media/images/svg/campaign.svg';
import { ReactComponent as Approval } from '../../media/images/svg/approval.svg';
import { ReactComponent as Monitoring } from '../../media/images/svg/monitoring.svg';

const GROUPS = [
    {
        label: 'Operations & service',
        tiles: [
            { key: 'Work Orders', Icon: Handyman },
            { key: 'Maintenance', Icon: Engineering },
            { key: 'Housekeeping', Icon: Cleaning },
            { key: 'Staff Tasks', Icon: TaskAlt },
            { key: 'Vendors', Icon: Storefront },
            { key: 'Inventory', Icon: Inventory },
            { key: 'Inspections', Icon: FactCheck },
            { key: 'Loading Dock', Icon: Forklift }
        ]
    },
    {
        label: 'Security & administration',
        tiles: [
            { key: 'Front Desk', Icon: Badge },
            { key: 'Keys & Access', Icon: Key },
            { key: 'Parking', Icon: Parking },
            { key: 'Incidents', Icon: Report },
            { key: 'Emergency', Icon: Campaign },
            { key: 'Approvals', Icon: Approval },
            { key: 'Analytics', Icon: Monitoring }
        ]
    }
];

const PACKAGES_AT_DESK = 14;
const RESIDENTS_ENABLED = 412;

function greeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
}

/* Custom Property Dashboard: the staff home screen. Every figure here is
   read from the same records the module screens change. */
function OverviewScreen() {
    const { workOrders, visitors, approvals, moves, alert, go } = useBuilding();

    const open = workOrders.filter(w => w.status !== 'Done');
    const urgent = open.filter(w => w.priority === 'High');
    const expected = visitors.filter(v => v.date === day(0) && v.status === 'Expected');
    const pending = approvals.filter(a => a.status === 'Pending');
    const pendingMoves = moves.filter(m => m.status === 'Pending');

    const attention = [
        ...urgent.map(w => ({ id: `wo-${w.id}`, title: w.title, meta: `${w.unit} · ${w.category} · opened ${w.opened}`, tone: 'alert', chip: 'Urgent', screen: 'Work Orders' })),
        ...pending.map(a => ({ id: `ap-${a.id}`, title: a.title, meta: `${a.kind} · ${a.from}${a.amount ? ` · ${a.amount}` : ''}`, tone: 'warn', chip: 'Awaiting approval', screen: 'Approvals' })),
        ...pendingMoves.map(m => ({ id: `mv-${m.id}`, title: `${m.kind}, unit ${m.unit}`, meta: `${m.date} · ${m.slot} · elevator not yet reserved`, tone: 'warn', chip: 'Unconfirmed', screen: 'Loading Dock' })),
        { id: 'coi', title: 'Coastal AV insurance certificate', meta: `COI expires ${day(3)} · on site tomorrow`, tone: 'warn', chip: 'Expiring', screen: 'Vendors' },
        { id: 'fire', title: 'Fire extinguisher monthly check', meta: `Floors 10 to 18 due ${day(1)}`, tone: 'info', chip: 'Due soon', screen: 'Inspections' },
        { id: 'pass', title: 'Guest pass FL KXR-4471', meta: `Unit 1502 guest · expires ${day(0)} at 6:00 PM`, tone: 'info', chip: 'Expires today', screen: 'Parking' }
    ];

    return (
        <div className="screen-content">
            <ScreenHead
                eyebrow="Property dashboard"
                title={`${greeting()}. ${open.length} open work ${open.length === 1 ? 'order' : 'orders'}, ${expected.length} ${expected.length === 1 ? 'visitor' : 'visitors'} expected today.`}
            />

            <div className="banner">
                <img src={Skyline} alt="" />
                <p className="banner__caption">428 residents · 38 floors · 6 amenity spaces</p>
            </div>

            {alert && (
                <Panel className="panel--accent" style={{ marginBottom: '1rem' }}>
                    <div className="row__end" style={{ justifyContent: 'space-between' }}>
                        <h4>{alert.title}</h4>
                        <Chip tone={alert.level === 'emergency' ? 'alert' : 'info'}>
                            {alert.level === 'emergency' ? 'Emergency alert live' : 'Advisory live'}
                        </Chip>
                    </div>
                    <p className="row__meta">Sent {alert.sentAt} · showing on every resident screen now.</p>
                    <button type="button" className="btn btn-outline-light btn-sm" onClick={() => go('Emergency')}>Manage alert</button>
                </Panel>
            )}

            <Kpis items={[
                { label: 'Open work orders', value: open.length, note: `${urgent.length} urgent`, tone: urgent.length ? 'alert' : 'info' },
                { label: 'Visitors today', value: expected.length, note: 'expected at the desk' },
                { label: 'Pending approvals', value: pending.length, tone: pending.length ? 'warn' : 'ok' },
                { label: 'Packages at desk', value: PACKAGES_AT_DESK, note: '3 held over 5 days' },
                { label: 'Residents on app', value: RESIDENTS_ENABLED, note: '96% of 428 residents', tone: 'ok' }
            ]} />

            <div className="stack">
                <h4>Needs attention</h4>
                {attention.length === 0 ? <Empty>Nothing waiting on you. Enjoy the quiet.</Empty> : (
                    <ul className="rows">
                        {attention.map(item => (
                            <li className="row" key={item.id}>
                                <div className="row__main">
                                    <span className="row__title">{item.title}</span>
                                    <span className="row__meta">{item.meta}</span>
                                </div>
                                <div className="row__end">
                                    <Chip tone={item.tone}>{item.chip}</Chip>
                                    <button type="button" className="btn btn-outline-light btn-sm" onClick={() => go(item.screen)}>Open</button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {GROUPS.map(group => (
                <div className="tiles__group" key={group.label}>
                    <span className="tiles__label">{group.label}</span>
                    <div className="tiles">
                        {group.tiles.map(({ key, Icon }) => (
                            <button type="button" className="tile" key={key} onClick={() => go(key)}>
                                <Icon aria-hidden="true" />
                                {key}
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default OverviewScreen;
